/* eslint-disable unicorn/prefer-module */
import { useCallback, useMemo, useState } from "react";
import { FaInfoCircle } from "react-icons/fa";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { BadgeDato, CategoriaLeyendaIpsos, Loader } from "..";
import {
  CONSUMO_GATOS,
  CONSUMO_PERROS,
  DISTRIBUCION_TAMANO_PERROS,
  ESCENARIOS_CAPTACION_MASCOTAS,
  IPSOS_MASCOTAS,
  PCT_GATOS_DE_MASCOTAS,
  PCT_PERROS_DE_MASCOTAS,
  PRECIOS_ALIMENTO_DEFAULT,
} from "../../data/ipsos2025/mascotas";
import { useCategoriaMoneda, useUniversoCategorias } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaMascotas.module.scss";

export const Mascotas = () => {
  const { formatNumber, formatLocal, convertCLP, pais } = useCategoriaMoneda();
  const {
    edificios,
    loading,
    error,
    checkAll,
    checkedBuildings,
    toggleCheck,
    toggleCheckAll,
    cantidadEdificios,
    totalPantallas,
    totalEjecutivos,
    totalEmpresas,
  } = useUniversoCategorias(pais);
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [vistaActiva, setVistaActiva] = useState("perros"); // 'perros' o 'gatos'
  const [verAnual, setVerAnual] = useState(false);
  const [showOportunidadModal, setShowOportunidadModal] = useState(false);

  // Estado para simulador
  const [calidadAlimento, setCalidadAlimento] = useState("superpremium");
  const [porcentajeCaptacion, setPorcentajeCaptacion] = useState(4);
  const [marcaSeleccionada, setMarcaSeleccionada] = useState("Royal Canin");

  // Estado para distribución de mercado por segmento (debe sumar 100%)
  const [distribucionMercado, setDistribucionMercado] = useState({
    superpremium: 20,
    premium: 35,
    estandar: 45,
  });

  // Función para actualizar distribución manteniendo suma = 100%
  // Los valores deben ser múltiplos de 5 para coincidir con las opciones del select
  const actualizarDistribucion = useCallback((segmento, nuevoValor) => {
    setDistribucionMercado((prev) => {
      const segmentos = ["superpremium", "premium", "estandar"];
      const otros = segmentos.filter((k) => k !== segmento);
      const sumaOtros = otros.reduce((sum, k) => sum + prev[k], 0);
      const disponible = 100 - nuevoValor;

      // Redondear a múltiplo de 5 más cercano
      const redondearA5 = (n) => Math.round(n / 5) * 5;

      if (sumaOtros === 0) {
        // Si los otros son 0, distribuir equitativamente
        const valorPrimero = redondearA5(disponible / 2);
        return {
          [segmento]: nuevoValor,
          [otros[0]]: valorPrimero,
          [otros[1]]: disponible - valorPrimero,
        };
      }

      // Distribuir proporcionalmente entre los otros (redondeando a múltiplos de 5)
      const proporcion0 = prev[otros[0]] / sumaOtros;
      const valor0 = redondearA5(disponible * proporcion0);
      const valor1 = disponible - valor0;

      return {
        [segmento]: nuevoValor,
        [otros[0]]: valor0,
        [otros[1]]: valor1,
      };
    });
  }, []);

  // =====================================================
  // CÁLCULOS GENERALES MASCOTAS
  // =====================================================
  const resultadosBase = useMemo(() => {
    if (!totalEjecutivos) return null;

    const tienenMascotas = Math.round(totalEjecutivos * IPSOS_MASCOTAS.penetracion.valor);
    const noTienen = Math.round(totalEjecutivos * IPSOS_MASCOTAS.noPenetracion.valor);

    // Cantidad de mascotas
    const conUna = Math.round(tienenMascotas * IPSOS_MASCOTAS.cantidadMascotas.una.valor);
    const conDos = Math.round(tienenMascotas * IPSOS_MASCOTAS.cantidadMascotas.dos.valor);
    const conTres = Math.round(tienenMascotas * IPSOS_MASCOTAS.cantidadMascotas.tres.valor);
    const conCuatroMas = Math.round(tienenMascotas * IPSOS_MASCOTAS.cantidadMascotas.cuatroMas.valor);
    const totalMascotas = conUna * 1 + conDos * 2 + conTres * 3 + conCuatroMas * 4.5;

    // Tipos de mascota
    const tienenPerro = Math.round(tienenMascotas * IPSOS_MASCOTAS.tipoMascota.perro.valor);
    const tienenGato = Math.round(tienenMascotas * IPSOS_MASCOTAS.tipoMascota.gato.valor);

    // Estimación de número de perros y gatos — supuesto comercial, no Ipsos (distribución proporcional)
    const totalPerros = Math.round(totalMascotas * PCT_PERROS_DE_MASCOTAS.valor);
    const totalGatos = Math.round(totalMascotas * PCT_GATOS_DE_MASCOTAS.valor);

    return {
      tienenMascotas,
      noTienen,
      cantidadMascotas: {
        una: conUna,
        dos: conDos,
        tres: conTres,
        cuatroMas: conCuatroMas,
      },
      totalMascotas: Math.round(totalMascotas),
      tienenPerro,
      tienenGato,
      totalPerros,
      totalGatos,
    };
  }, [totalEjecutivos]);

  // =====================================================
  // CÁLCULOS PERROS
  // =====================================================
  const resultadosPerros = useMemo(() => {
    if (!resultadosBase) return null;

    const { tienenPerro, totalPerros } = resultadosBase;

    const perrosPorTamano = {};
    let consumoTotalKgMes = 0;

    Object.entries(DISTRIBUCION_TAMANO_PERROS).forEach(([tamano, pctDato]) => {
      const cantidad = Math.round(totalPerros * pctDato.valor);
      const consumoMes = cantidad * CONSUMO_PERROS[tamano].consumoMensual;
      perrosPorTamano[tamano] = {
        cantidad,
        consumoMes,
        consumoAnual: consumoMes * 12,
      };
      consumoTotalKgMes += consumoMes;
    });

    // Calcular mercado por calidad de alimento (usando distribución de mercado)
    const mercadoPorCalidad = {};
    Object.entries(PRECIOS_ALIMENTO_DEFAULT.perros).forEach(([calidad, data]) => {
      const kgSegmento = consumoTotalKgMes * (distribucionMercado[calidad] / 100);
      mercadoPorCalidad[calidad] = {
        nombre: data.nombre,
        precioKg: data.precioKg,
        kgMensual: kgSegmento,
        mercadoMensual: kgSegmento * data.precioKg,
        mercadoAnual: kgSegmento * data.precioKg * 12,
      };
    });

    // Market share marcas
    const marketShareData = {};
    // Mercado promedio (usando superpremium como referencia)
    const mercadoReferenciaMsg = consumoTotalKgMes * PRECIOS_ALIMENTO_DEFAULT.perros.superpremium.precioKg;
    Object.entries(IPSOS_MASCOTAS.marketShareAlimento).forEach(([marca, dato]) => {
      marketShareData[marca] = {
        dato,
        share: dato.valor,
        mercadoMensual: Math.round(mercadoReferenciaMsg * dato.valor),
      };
    });

    return {
      tienenPerro,
      totalPerros,
      perrosPorTamano,
      consumoTotalKgMes,
      consumoTotalKgAnual: consumoTotalKgMes * 12,
      mercadoPorCalidad,
      marketShareData,
    };
  }, [resultadosBase, distribucionMercado]);

  // =====================================================
  // CÁLCULOS GATOS
  // =====================================================
  const resultadosGatos = useMemo(() => {
    if (!resultadosBase) return null;

    const { tienenGato, totalGatos } = resultadosBase;

    const consumoMes = totalGatos * CONSUMO_GATOS.adulto.consumoMensual;

    // Calcular mercado por calidad de alimento (usando distribución de mercado)
    const mercadoPorCalidad = {};
    Object.entries(PRECIOS_ALIMENTO_DEFAULT.gatos).forEach(([calidad, data]) => {
      const kgSegmento = consumoMes * (distribucionMercado[calidad] / 100);
      mercadoPorCalidad[calidad] = {
        nombre: data.nombre,
        precioKg: data.precioKg,
        kgMensual: kgSegmento,
        mercadoMensual: kgSegmento * data.precioKg,
        mercadoAnual: kgSegmento * data.precioKg * 12,
      };
    });

    // Market share marcas
    const marketShareData = {};
    const mercadoReferenciaMsg = consumoMes * PRECIOS_ALIMENTO_DEFAULT.gatos.superpremium.precioKg;
    Object.entries(IPSOS_MASCOTAS.marketShareAlimento).forEach(([marca, dato]) => {
      marketShareData[marca] = {
        dato,
        share: dato.valor,
        mercadoMensual: Math.round(mercadoReferenciaMsg * dato.valor),
      };
    });

    return {
      tienenGato,
      totalGatos,
      consumoMes,
      consumoAnual: consumoMes * 12,
      mercadoPorCalidad,
      marketShareData,
    };
  }, [resultadosBase, distribucionMercado]);

  // =====================================================
  // CÁLCULO OPORTUNIDAD
  // =====================================================
  const calcularOportunidad = useCallback(() => {
    if (vistaActiva === "perros" && resultadosPerros) {
      const mercadoBase = resultadosPerros.mercadoPorCalidad[calidadAlimento];
      if (!mercadoBase) return null;

      const mercadoCaptadoMensual = Math.round(mercadoBase.mercadoMensual * (porcentajeCaptacion / 100));

      return {
        mercadoTotal: mercadoBase.mercadoMensual,
        mercadoCaptadoMensual,
        mercadoCaptadoAnual: mercadoCaptadoMensual * 12,
        kgMensuales: Math.round(resultadosPerros.consumoTotalKgMes * (porcentajeCaptacion / 100)),
      };
    } else if (vistaActiva === "gatos" && resultadosGatos) {
      const mercadoBase = resultadosGatos.mercadoPorCalidad[calidadAlimento];
      if (!mercadoBase) return null;

      const mercadoCaptadoMensual = Math.round(mercadoBase.mercadoMensual * (porcentajeCaptacion / 100));

      return {
        mercadoTotal: mercadoBase.mercadoMensual,
        mercadoCaptadoMensual,
        mercadoCaptadoAnual: mercadoCaptadoMensual * 12,
        kgMensuales: Math.round(resultadosGatos.consumoMes * (porcentajeCaptacion / 100)),
      };
    }
    return null;
  }, [vistaActiva, resultadosPerros, resultadosGatos, calidadAlimento, porcentajeCaptacion]);

  if (loading) return <Loader />;
  if (error) return <p className={styles.disclaimer}>{error}</p>;

  return (
    <div className={styles.mascotasPage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>ALIMENTO MASCOTAS</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <CategoriaLeyendaIpsos />

      <div className={styles.mascotasContent}>
        {/* Resumen de penetración */}
        {resultadosBase && (
          <div className={styles.resumenPenetracion}>
            <div className={styles.resumenCard}>
              <div className={styles.resumenValor}>
                <span className={styles.valorDestacado}>{formatNumber(resultadosBase.tienenMascotas)}</span>
              </div>
              <div className={styles.resumenLabel}>Tienen mascotas</div>
              <div className={styles.resumenDetalle}>
                <BadgeDato dato={IPSOS_MASCOTAS.penetracion}>
                  {(IPSOS_MASCOTAS.penetracion.valor * 100).toFixed(0)}%
                </BadgeDato>{" "}
                de ejecutivos
              </div>
            </div>
            <div className={styles.resumenCard}>
              <div className={styles.resumenValor}>
                <span className={styles.valorDestacado}>{formatNumber(resultadosBase.totalMascotas)}</span>
                <span className={styles.tooltipContainer}>
                  <FaInfoCircle className={styles.infoIcon} />
                  <span className={styles.tooltipText}>
                    Promedio ~2 mascotas por hogar (47% tiene 1, 29% tiene 2, 9% tiene 3, 15% tiene 4+). Distribución
                    estimada: 60% perros, 35% gatos, 5% otros.
                  </span>
                </span>
              </div>
              <div className={styles.resumenLabel}>Total mascotas estimadas</div>
              <div className={styles.resumenDetalle}>
                {formatNumber(resultadosBase.totalPerros)} perros | {formatNumber(resultadosBase.totalGatos)} gatos
              </div>
            </div>
            <div className={styles.resumenCard}>
              <div className={styles.resumenValor}>
                <BadgeDato dato={IPSOS_MASCOTAS.importancia.muyImportante} className={styles.badgeIpsosGrande}>
                  {(IPSOS_MASCOTAS.importancia.muyImportante.valor * 100).toFixed(0)}%
                </BadgeDato>
              </div>
              <div className={styles.resumenLabel}>Considera muy importante</div>
              <div className={styles.resumenLabel}>su mascota en la familia</div>
            </div>
          </div>
        )}

        {/* Tabs para cambiar entre Perros y Gatos */}
        <div className={styles.tabsContainer}>
          <button
            type="button"
            className={cx(styles.tab, vistaActiva === "perros" && styles.tabActivo)}
            onClick={() => setVistaActiva("perros")}
          >
            Perros
          </button>
          <button
            type="button"
            className={cx(styles.tab, vistaActiva === "gatos" && styles.tabActivo)}
            onClick={() => setVistaActiva("gatos")}
          >
            Gatos
          </button>
        </div>

        {/* =====================================================
            VISTA PERROS
            ===================================================== */}
        {vistaActiva === "perros" && resultadosPerros && (
          <>
            {/* Info general */}
            <div className={styles.infoSection}>
              <div className={styles.infoRow}>
                <BadgeDato dato={IPSOS_MASCOTAS.tipoMascota.perro}>
                  {(IPSOS_MASCOTAS.tipoMascota.perro.valor * 100).toFixed(0)}%
                </BadgeDato>
                <span>
                  de quienes tienen mascotas tienen al menos un perro → al menos{" "}
                  <strong>{formatNumber(resultadosPerros.tienenPerro)}</strong> dueños de perros (
                  {formatNumber(resultadosPerros.totalPerros)} perros estimados)
                </span>
              </div>
            </div>

            {/* Tabla de consumo por tamaño */}
            <div className={styles.tablaContainer}>
              <h4 className={styles.subtitulo}>
                Consumo de Alimento por Tamaño
                <span className={styles.tooltipContainer}>
                  <FaInfoCircle className={styles.infoIcon} />
                  <span className={styles.tooltipTextWide}>
                    <strong>Distribución por tamaño</strong> (estimación estándar de mercado):
                    <br />• Grande (25%): razas tipo Labrador, Pastor Alemán
                    <br />• Mediano (45%): razas tipo Beagle, Cocker
                    <br />• Chico (30%): razas tipo Chihuahua, Yorkshire
                    <br />
                    <br />
                    <strong>Consumo mensual</strong> (recomendaciones fabricantes):
                    <br />• Grande (25-35 kg): ~15 kg/mes
                    <br />• Mediano (10-25 kg): ~9 kg/mes
                    <br />• Chico (&lt;10 kg): ~5 kg/mes
                    <br />
                    <br />
                    <strong>Cálculo:</strong> Kg totales = Cantidad × Consumo mensual
                  </span>
                </span>
              </h4>
              <table className={cx("table table-sm", styles.tablaResultados)}>
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">Tamaño</th>
                    <th className="text-center">Peso mascota</th>
                    <th className="text-center">Cantidad perros</th>
                    <th className="text-center">Consumo/mes (kg)</th>
                    <th className="text-center">Kg/mes</th>
                    <th className="text-center">Kg/año</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(CONSUMO_PERROS).map(([tamano, data]) => {
                    const resultado = resultadosPerros.perrosPorTamano[tamano];
                    return (
                      <tr key={tamano}>
                        <td className="text-center text-capitalize">
                          <strong>{tamano}</strong>
                        </td>
                        <td className="text-center">{data.peso}</td>
                        <td className="text-center">{formatNumber(resultado.cantidad)}</td>
                        <td className="text-center">{data.consumoMensual}</td>
                        <td className="text-center">
                          <strong>{formatNumber(resultado.consumoMes)}</strong>
                        </td>
                        <td className="text-center">
                          <strong>{formatNumber(resultado.consumoAnual)}</strong>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className={styles.filaTotales}>
                    <td colSpan={4} />
                    <td className={cx("text-center", styles.celdaTotal)}>
                      <strong>{formatNumber(resultadosPerros.consumoTotalKgMes)}</strong>
                    </td>
                    <td className={cx("text-center", styles.celdaTotal)}>
                      <strong>{formatNumber(resultadosPerros.consumoTotalKgAnual)}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mercado por segmento */}
            <div className={styles.mercadoCalidadContainer}>
              <div className={styles.mercadoHeader}>
                <h4 className={styles.subtitulo}>Mercado por Segmento</h4>
                <div className={styles.mercadoToggle}>
                  <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>Mensual</span>
                  <label className={styles.toggleSwitch}>
                    <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                    <span className={styles.toggleSlider} />
                  </label>
                  <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>Anual</span>
                </div>
                <span className={styles.mercadoTotalValor}>
                  {formatLocal(
                    convertCLP(
                      Object.values(resultadosPerros.mercadoPorCalidad).reduce(
                        (sum, d) => sum + (verAnual ? d.mercadoAnual : d.mercadoMensual),
                        0
                      )
                    )
                  )}
                </span>
              </div>
              <div className={styles.mercadoCalidadGrid}>
                {Object.entries(resultadosPerros.mercadoPorCalidad).map(([calidad, data]) => (
                  <div key={calidad} className={styles.mercadoCalidadCard}>
                    <div className={styles.distribucionSelect}>
                      <select
                        value={distribucionMercado[calidad]}
                        onChange={(e) => actualizarDistribucion(calidad, Number(e.target.value))}
                        className={styles.selectDistribucion}
                      >
                        {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100].map(
                          (pct) => (
                            <option key={pct} value={pct}>
                              {pct}%
                            </option>
                          )
                        )}
                      </select>
                    </div>
                    <div className={styles.calidadNombre}>{data.nombre}</div>
                    <div className={styles.calidadPrecio}>{formatLocal(convertCLP(data.precioKg))}/kg</div>
                    <div className={styles.calidadMercado}>
                      <span className={styles.mercadoLabel}>Mercado:</span>
                      <span className={styles.mercadoValor}>
                        {formatLocal(convertCLP(verAnual ? data.mercadoAnual : data.mercadoMensual))}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Market Share + Canales de Compra */}
            <div className={styles.shareCanalesRow}>
              {/* Market Share - 70% */}
              <div className={styles.marketShareSection}>
                <h4 className={styles.subtitulo}>Marca de alimento para mascotas (Ipsos, todas las mascotas)</h4>
                <div className={styles.marketShareGrid}>
                  {Object.entries(resultadosPerros.marketShareData)
                    .slice(0, 9)
                    .map(([nombre, data]) => (
                      <div key={nombre} className={styles.marketShareCard}>
                        <span className={styles.cardNombre}>{nombre}</span>
                        <BadgeDato dato={data.dato}>{(data.share * 100).toFixed(0)}%</BadgeDato>
                        <span className={styles.cardMercado}>
                          {formatLocal(convertCLP(verAnual ? data.mercadoMensual * 12 : data.mercadoMensual))}
                        </span>
                      </div>
                    ))}
                </div>
              </div>

              {/* Canales de Compra - 30% */}
              <div className={styles.canalesSection}>
                <h4 className={styles.subtitulo}>Canales de Compra</h4>
                <div className={styles.canalesChart}>
                  {Object.entries(IPSOS_MASCOTAS.canalCompra).map(([canal, dato]) => (
                    <div key={canal} className={styles.canalBarItem}>
                      <div className={styles.canalBarLabel}>
                        <span>{canal}</span>
                        <span className={styles.canalBarPct}>{(dato.valor * 100).toFixed(0)}%</span>
                      </div>
                      <div className={styles.canalBarTrack}>
                        <div className={styles.canalBarFill} style={{ width: `${dato.valor * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <button type="button" className={styles.simularBtn} onClick={() => setShowOportunidadModal(true)}>
              Simular Oportunidad
            </button>

            {/* Disclaimer */}
            <div className={styles.disclaimer}>
              <p>
                <strong>Ipsos 2025 (lámina 79, base 400/248):</strong> el 71,1% de quienes tienen mascotas tienen perro.
                Frecuencia de compra: 72,8% compra alimento mensualmente.
              </p>
              <p>
                <strong>Supuestos comerciales:</strong> distribución perros/gatos, distribución por tamaño de perro,
                consumo mensual por tamaño y precios por kg.
              </p>
            </div>
          </>
        )}

        {/* =====================================================
            VISTA GATOS - EN CONSTRUCCIÓN
            ===================================================== */}
        {vistaActiva === "gatos" && (
          <div className={styles.enConstruccion}>
            <span className={styles.enConstruccionIcono}>🚧</span>
            <h4>Sección en construcción</h4>
            <p>El análisis de mercado de alimento para gatos estará disponible próximamente.</p>
          </div>
        )}
      </div>

      {/* Modal de Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Inventario de Edificios</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> ejecutivos
                </span>
                <button type="button" className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">✓</th>
                    <th className="text-center">Edificio</th>
                    <th className="text-center">Ubicación</th>
                    <th className="text-center">Pantallas</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(({ id, edificio, ubicacion, personas, pantallas, espera, totems, cowork }) => (
                    <tr
                      key={id}
                      onClick={() => toggleCheck(id)}
                      className={cx(checkedBuildings[id] && "table-success")}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                      </td>
                      <td className="text-center">{edificio.toUpperCase()}</td>
                      <td className="text-center">{ubicacion}</td>
                      <td className="text-center">
                        {formatNumber((pantallas || 0) + (espera || 0) + (totems || 0) + (cowork || 0))}
                      </td>
                      <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Oportunidad */}
      {showOportunidadModal && (resultadosPerros || resultadosGatos) && (
        <div className={styles.modalOverlay} onClick={() => setShowOportunidadModal(false)}>
          <div className={cx(styles.modalContent, styles.modalOportunidad)} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Simulador de Oportunidad - Alimento para {vistaActiva === "perros" ? "Perros" : "Gatos"}</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowOportunidadModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.simuladorContainer}>
                <div className={styles.simuladorControles}>
                  {vistaActiva === "perros" && (
                    <div className={styles.controlGroup}>
                      <label>Marca objetivo:</label>
                      <select
                        value={marcaSeleccionada}
                        onChange={(e) => setMarcaSeleccionada(e.target.value)}
                        className="form-select form-select-sm"
                      >
                        {Object.keys(IPSOS_MASCOTAS.marketShareAlimento).map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {vistaActiva === "gatos" && (
                    <div className={styles.controlGroup}>
                      <label>Marca objetivo:</label>
                      <select
                        value={marcaSeleccionada}
                        onChange={(e) => setMarcaSeleccionada(e.target.value)}
                        className="form-select form-select-sm"
                      >
                        {Object.keys(IPSOS_MASCOTAS.marketShareAlimento).map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className={styles.controlGroup}>
                    <label>Calidad alimento:</label>
                    <select
                      value={calidadAlimento}
                      onChange={(e) => setCalidadAlimento(e.target.value)}
                      className="form-select form-select-sm"
                    >
                      <option value="premium">Premium</option>
                      <option value="superpremium">Super Premium</option>
                      <option value="estandar">Estándar</option>
                    </select>
                  </div>
                  <div className={styles.controlGroup}>
                    <label>% captación mercado (supuesto):</label>
                    <select
                      value={porcentajeCaptacion}
                      onChange={(e) => setPorcentajeCaptacion(Number(e.target.value))}
                      className="form-select form-select-sm"
                    >
                      {ESCENARIOS_CAPTACION_MASCOTAS.map((p) => (
                        <option key={p.valor} value={p.valor}>
                          {p.valor}%
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {(() => {
                  const oportunidad = calcularOportunidad();
                  if (!oportunidad) return null;

                  return (
                    <div className={styles.simuladorResultados}>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Mercado total mensual:</span>
                        <span className={styles.resultadoValor}>
                          {formatLocal(convertCLP(oportunidad.mercadoTotal))}
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Kg a captar/mes:</span>
                        <span className={styles.resultadoValor}>{formatNumber(oportunidad.kgMensuales)} kg</span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Captación al {porcentajeCaptacion}%:</span>
                        <span className={styles.resultadoValorDestacado}>
                          {formatLocal(convertCLP(oportunidad.mercadoCaptadoMensual))}/mes
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Mercado captado anual:</span>
                        <span className={styles.resultadoValorDestacado}>
                          {formatLocal(convertCLP(oportunidad.mercadoCaptadoAnual))}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowOportunidadModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Mascotas;
